<script setup lang="ts">
import { faImage } from "@fortawesome/pro-light-svg-icons";
import { faBolt } from "@fortawesome/pro-solid-svg-icons";
import MediaLibrary from "~/components/MediaLibrary.vue";
import type { Media } from "~/stores/media-library";
import type { FieldDefinition } from "~/models/Automation";

const props = defineProps<{
    fields?: FieldDefinition[];
    buttonClass?: string;
    imageClass?: string;
}>();
const image = defineModel<string>("modelValue", { required: true });

function chooseImage(media: Media, toggle: () => void) {
    image.value = media.full_url;
    toggle();
}

const images = computed(() =>
    (props.fields ?? [])
        .filter((field) => field.sub_type === "image")
        .map(
            (field): Media => ({
                id: field.key,
                collection: "custom",
                name: field.label,
                file_name: field.label,
                mime_type: "image/png",
                size: 0,
                metadata: field as unknown as Media["metadata"],
                relative_url: field.default_value as string,
                full_url: makeVariable(field.key) as string,
                preview_url: field.default_value as string,
                api_url: field.default_value as string,
                created_at: new Date().toString(),
                updated_at: new Date().toString(),
            }),
        ),
);

const imageField = computed(() =>
    images.value.find((img) => image.value === img.full_url),
);
</script>

<template>
    <MediaLibrary start-at="custom" :custom="images" custom-label="Fields">
        <template #custom-icon>
            <FontAwesomeIcon :icon="faBolt" />
        </template>
        <template #default="{ toggle }">
            <UButton
                block
                class="justify-start"
                color="white"
                size="lg"
                :class="buttonClass"
                @click="toggle"
            >
                <template v-if="!image || typeof image !== 'string'" #leading>
                    <FontAwesomeIcon :icon="faImage" size="xl" />
                </template>
                <span v-if="!image">Choose image</span>
                <nuxt-img
                    v-else
                    :src="imageField ? imageField.preview_url : image"
                    class="h-24"
                    :class="imageClass"
                />
            </UButton>
        </template>
        <template #action="{ toggle, selected }">
            <UButton
                block
                color="primary"
                size="lg"
                @click="chooseImage(selected, toggle)"
            >
                Add media to field
            </UButton>
        </template>
    </MediaLibrary>
</template>

<style scoped></style>
